import { Observable, ReplaySubject, combineLatest, connectable, merge } from 'rxjs';
import { debounceTime, filter, map } from 'rxjs/operators';
import { fastBuffer } from '../utils';
import { KeyboardCodes } from './keyboard';
import { KeyboardItemDirective } from './keyboard-item.directive';

const TYPE_AHEAD_DEBOUNCE_TIME = 1000;


export class KeyboardUtils {
    /**
     * Prüft ob entsprechende Action mit Tastatur-Event in Verbindung gesetzt sind
     */
    static isKeyboardAction(event, action: string) { // event:KeyboardEvent
        return KeyboardCodes[action] === event.keyCode;
    }

    /**
     * Prüft ob ein Tastaturzeichen suchbar ist, bzw. Numerisch oder Alphabetisch ist.
     */
    static keyboardEventSearchable(event) { // event:KeyboardEvent
        if (!event.key || typeof event.key !== 'string') {
            return false;
        }
        if (KeyboardUtils.isKeyboardAction(event, 'backspace')) {
            return true;
        }
        const isKey = /^[a-z0-9äöü]$/i;
        return isKey.test(event.key.toLowerCase());
    }

    /**
     * Anhand von Tastatur-Pfeil-Tasten (Oben, Unten) andern dementsprechend den Index.
     *
     * @param source$ KeyboardPipeSource
     */
    static arrowKeyToDirectionOperator<T>(source$: KeyboardPipeSource<T>): Observable<number> {
        const decrease$ = source$.pipe(
            filter(([e]) => KeyboardUtils.isKeyboardAction(e, 'up')),
            map(([_, index]) => index - 1));

        const increase$ = source$.pipe(
            filter(([e]) => KeyboardUtils.isKeyboardAction(e, 'down')),
            map(([_, index]) => index + 1));

        return merge(decrease$, increase$);
    }

    static tabKeyToDirectionOperator<T>(source$: KeyboardPipeSource<T>): Observable<number> {
        const decrease$ = source$.pipe(
            filter(([e]) => isKeyShiftTab(e)),
            map(([_, index]) => index - 1));

        const increase$ = source$.pipe(
            filter(([e]) => isKeyTab(e)),
            map(([_, index]) => index + 1));

        return merge(decrease$, increase$);
    }

    /**
     * Eine Hilfsfunktion, die anhand des Tastatur-Events entsprechende Item sucht.
     * Die Tastatur-Events werden in Zeitraum von eine Sekunde gesammelt (TYPE_AHEAD_DEBOUNCE_TIME).
     *
     * @param source$ KeyboardPipeSource
     */
    static findIndexBySymbolOperator<T>(source$: KeyboardPipeSource<T>): Observable<number> {
        const publishedSource$ = connectable(source$, { connector: () => new ReplaySubject(1), resetOnDisconnect: false })
        const debouncedSource$ = publishedSource$.pipe(
            debounceTime(TYPE_AHEAD_DEBOUNCE_TIME)
        );
        const searchWord$ = publishedSource$.pipe(
            filter(([e]) => KeyboardUtils.keyboardEventSearchable(e)),
            map(([e]) => e.key.toLowerCase()),
            // Der feuert "sofort" und sammelt "danach" eine Sekunde lang.
            fastBuffer(debouncedSource$),
            map(words => words.join(''))
        );
        const items$: Observable<KeyboardItemDirective<T>[]> = publishedSource$.pipe(
            filter(([e]) => KeyboardUtils.keyboardEventSearchable(e)),
            map(([_, __, items]) => items)
        );

        publishedSource$.connect();

        return combineLatest([searchWord$, items$]).pipe(
            map(([searchWord, items]) => items.findIndex(i => i.search(searchWord) === 0))
        );
    }
}

export type KeyboardPipeSource<T> = Observable<[KeyboardEvent, number, KeyboardItemDirective<T>[]]>;

function isKeyShiftTab(event: KeyboardEvent): boolean {
    return KeyboardUtils.isKeyboardAction(event, 'tab') && event.shiftKey === true;
}

function isKeyTab(event: KeyboardEvent): boolean {
    return KeyboardUtils.isKeyboardAction(event, 'tab') && event.shiftKey !== true;
}
